import React, { useState } from 'react';
import moment from 'moment';
import PropTypes, { any } from 'prop-types';
import Description from '../../../text/Description';
import useIsMobile from '../../../hooks/useIsMobile';
import { getPlayerName } from '../../../../utils';

import styles from './playerDetails.scss';
import Img from '../../../Img';

const PlayerDetails = props => {
    const {
        firstName,
        lastName,
        description,
        playerId,
        isCurrentMemberAdmin,
        showOrganizerViewForPTTEvent,
        showCollegeGradYear,
        college,
        gradYear,
        showTag,
        tag,
        showSelfProfileIcon,
        'data-testid': dataTestId,
    } = props;
    const utcNow = moment.utc();
    const [copied, setCopied] = useState(false);
    const [clicked, setClicked] = useState(false);
    const [showCopyIcon, setShowCopyIcon] = useState(false);
    const { isMobileWidth } = useIsMobile();

    const handleOnClick = (e, playerId) => {
        e.stopPropagation();
        e.preventDefault();
        setClicked(isMobileWidth);
        if (!isMobileWidth) {
            copyToClipboard(playerId);
        }
    };

    const handleMobileOnClick = (e, playerId) => {
        e.stopPropagation();
        e.preventDefault();
        setClicked(false);
        copyToClipboard(playerId);
    };

    const copyToClipboard = (playerId) => {
        navigator.clipboard.writeText(playerId).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1000);
        }).catch(() => {
            setCopied(false);
        });
    };

    const name = getPlayerName(firstName, lastName);

    const shouldShowOnlyCollege = showOrganizerViewForPTTEvent && !showCollegeGradYear && !!college;

    const shouldShowCollegeAndGradYear = showOrganizerViewForPTTEvent &&
        !!college &&
        !!gradYear &&
        moment(gradYear)?.isAfter(utcNow);

    return (
        <div className="name-and-place show-ellipsis">
            <div className="name d-flex aic mw0 p0">
                <span className="show-ellipsis mw0" data-testid={dataTestId && `${dataTestId}.name`}>
                    {name}
                </span>
                {showTag && tag && (
                    <span className={`${styles.tag} ml4`} data-testid={dataTestId && `${dataTestId}.tag`}>{tag}</span>
                )}
                {showSelfProfileIcon && (
                    <Img src="/img/v2/icons/icon-profile.svg" alt="Self Profile" className={`${styles.selfProfileIcon} ml4`} />
                )}
            </div>
            <div className="place show-ellipsis">
                <Description desc={description} data-testid={dataTestId && `${dataTestId}.place`} />
            </div>
            {shouldShowOnlyCollege && (
                <div className="place show-ellipsis" data-testid={dataTestId && `${dataTestId}.college-name`}>
                    {college}
                </div>
            )}
            {shouldShowCollegeAndGradYear && !shouldShowOnlyCollege && (
                <div className="place show-ellipsis" data-testid={dataTestId && `${dataTestId}.college-grad-year`}>{`${college} (\u2018${moment(gradYear).format('YY')})`}</div>
            )}
            {isCurrentMemberAdmin && playerId && (
                <div
                    className="place show-ellipsis link"
                    onClick={(e) => handleOnClick(e, playerId)}
                    onMouseEnter={() => setShowCopyIcon(true)}
                    onMouseLeave={() => setShowCopyIcon(false)}
                    data-testid={dataTestId && `${dataTestId}.player-id`}
                >
                    {`ID: ${playerId}`}
                    {isMobileWidth ? (
                        <>
                            {clicked && (
                                <div className={`${styles.mobileCopyTextContainer}`}>
                                    <div className={`${styles.mobileCopyTextBox}`} onClick={(e) => handleMobileOnClick(e, playerId)} data-testid={dataTestId && `${dataTestId}.mobile-copy-btn`}>
                                        <span className="far fa-clone mr4" />Copy ID
                                    </div>
                                </div>
                            )}
                            {copied && (
                                <div className={`${styles.mobileCopyTextContainer} ${styles.onClick}`}>
                                    <div className={`${styles.mobileCopyTextBox} text-dark fs14`} data-testid={dataTestId && `${dataTestId}.mobile-copied-text`}>
                                        ID Copied
                                    </div>
                                </div>
                            )}
                        </>
                    ) : (
                        <>
                            {showCopyIcon && <span className="far fa-clone ml4" />}
                            {copied && (
                                <div className={`${styles.copyTextContainer}`}>
                                    <div className={`${styles.copyTextArrow}`} />
                                    <div className={`${styles.copyTextBox} text-dark fs14`} data-testid={dataTestId && `${dataTestId}.copied-text`}>
                                        ID Copied
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

PlayerDetails.propTypes = {
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    description: PropTypes.arrayOf(any),
    playerId: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
    ]),
    isCurrentMemberAdmin: PropTypes.bool,
    showOrganizerViewForPTTEvent: PropTypes.bool,
    college: PropTypes.string,
    gradYear: PropTypes.string,
    showTag: PropTypes.bool,
    tag: PropTypes.string,
    showCollegeGradYear: PropTypes.bool,
    showSelfProfileIcon: PropTypes.bool,
    'data-testid': PropTypes.string,
};

PlayerDetails.defaultProps = {
    firstName: '',
    lastName: '',
    description: [],
    playerId: null,
    isCurrentMemberAdmin: false,
    showOrganizerViewForPTTEvent: false,
    college: null,
    gradYear: null,
    showTag: false,
    tag: null,
    showCollegeGradYear: true,
    showSelfProfileIcon: false,
    'data-testid': null,
};

export default PlayerDetails;
